import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useImportLots, useAuction } from '../hooks/useAuctions';
import DocumentUploadForm from '../../../components/Documents/DocumentUploadForm';
import { documents as documentsApi } from '../../../utils/api';

const formatCurrency = (amount, currency, forceDecimals = false) => {
  if (!amount) return '-';
  
  // Check if amount has decimal places
  const hasDecimals = amount % 1 !== 0;
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: hasDecimals || forceDecimals ? 2 : 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export default function ImportLotsModal({ isOpen, onClose, auctionId, wonLotsCount }) {
  const [totalPrice, setTotalPrice] = useState('');
  const [error, setError] = useState('');
  const { mutate: importLots, isLoading: isImporting } = useImportLots();
  const { data: auction, isLoading } = useAuction(auctionId);
  const [allocatedPrices, setAllocatedPrices] = useState({});
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  // Update allocated prices whenever total price changes
  useEffect(() => {
    if (!auction?.lots || !totalPrice) {
      setAllocatedPrices({});
      return;
    }

    // Clean price for calculation by removing any non-numeric characters except decimal
    const cleanPrice = totalPrice.replace(/[^\d.]/g, '');
    if (isNaN(parseFloat(cleanPrice))) {
      setAllocatedPrices({});
      return;
    }

    const wonLots = auction.lots.filter(lot => lot.status === 'won');
    const totalWinningBidsInCents = wonLots.reduce((sum, lot) => {
      const bidInCents = Math.round((parseFloat(lot.winning_bid) || 0) * 100);
      return sum + bidInCents;
    }, 0);
    
    if (totalWinningBidsInCents === 0) {
      setAllocatedPrices({});
      return;
    }

    const totalPriceInCents = Math.round(parseFloat(cleanPrice) * 100);
    // Calculate initial allocations
    const newPrices = wonLots.reduce((acc, lot) => {
      const bidInCents = Math.round((parseFloat(lot.winning_bid) || 0) * 100);
      acc[lot.id] = parseFloat(((bidInCents / totalWinningBidsInCents) * totalPriceInCents / 100).toFixed(2));
      return acc;
    }, {});

    // Find the total allocated after rounding
    const allocatedTotal = Object.values(newPrices).reduce((sum, price) => sum + price, 0);
    
    // Calculate any remaining amount from rounding
    const remainingAmount = parseFloat((parseFloat(totalPrice) - allocatedTotal).toFixed(2));
    
    if (remainingAmount !== 0) {
      // Find the lot with the highest allocation to add the remainder to
      const [highestLotId] = Object.entries(newPrices).reduce(
        ([maxId, maxValue], [id, value]) => 
          value > maxValue ? [id, value] : [maxId, maxValue],
        [null, -Infinity]
      );
      
      // Add the remainder to the highest allocation
      if (highestLotId) {
        newPrices[highestLotId] = parseFloat((newPrices[highestLotId] + remainingAmount).toFixed(2));
      }
    }

    setAllocatedPrices(newPrices);
  }, [auction?.lots, totalPrice]);

  const handleDocumentUploadSuccess = (document) => {
    setUploadedDocuments(prev => [...prev, document]);
    setShowDocumentUpload(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Clean and validate total price - remove any non-numeric characters except decimal point
    const cleanTotalPrice = totalPrice.replace(/[^\d.]/g, '');
    const parsedTotalPrice = parseFloat(cleanTotalPrice);
    if (isNaN(parsedTotalPrice)) {
      setError('Please enter a valid total price');
      return;
    }
    
    const purchaseDate = new Date().toISOString();
    
    const wonLots = auction.lots.filter(lot => lot.status === 'won');
    const totalWinningBidsInCents = wonLots.reduce((sum, lot) => {
      const bidInCents = Math.round((parseFloat(lot.winning_bid) || 0) * 100);
      return sum + bidInCents;
    }, 0);
    
    // Calculate allocated prices using cleaned price
    const totalPriceInCents = Math.round(parsedTotalPrice * 100);
    const lotPrices = wonLots.reduce((acc, lot) => {
      const bidInCents = Math.round((parseFloat(lot.winning_bid) || 0) * 100);
      // Round to 2 decimal places after calculation
      acc[lot.id] = parseFloat(((bidInCents / totalWinningBidsInCents) * totalPriceInCents / 100).toFixed(2));
      return acc;
    }, {});

    // Use the same allocated prices shown in the UI
    const importData = {
      auctionId,
      data: {
        purchase_date: purchaseDate,
        lots: auction.lots
          .filter(lot => lot.status === 'won')
          .map(lot => ({
            id: lot.id,
            purchase_price: allocatedPrices[lot.id]
          }))
      }
    };

    importLots(
      importData,
      {
        onSuccess: async (response) => {
          // After coins are created, associate documents if any
          if (uploadedDocuments.length > 0 && response?.coins?.length > 0) {
            try {
              // Get all coin IDs from the response
              const coinIds = response.coins.map(coin => coin.id);
              if (!coinIds.length) {
                console.error('No coin IDs found in response');
                throw new Error('No coins found in response');
              }
              
              // Update each document with all coin IDs
              // Update each document with all coin IDs
              await Promise.all(uploadedDocuments.map(async doc => {
                const result = await documentsApi.update(doc.id, {
                  title: doc.title,
                  typeId: doc.type_id,
                  notes: doc.notes,
                  coinIds
                });
              }));
            } catch (err) {
              console.error('Failed to associate documents with coins:', err);
              setError('Documents uploaded but failed to associate with coins: ' + err.message);
            }
          }
          onClose();
        },
        onError: (err) => {
          setError(err.response?.data?.error || 'Failed to import lots');
        }
      }
    );
  };

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Import {wonLotsCount} Won {wonLotsCount === 1 ? 'Lot' : 'Lots'} to Inventory
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Enter the total price paid for all lots. The price will be allocated proportionally based on each lot's winning bid.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="mt-5 sm:mt-6">
                    {isLoading ? (
                      <div className="mb-4 text-sm text-gray-500">Loading lots...</div>
                    ) : auction?.lots ? (
                      <div className="mb-4 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Lot #
                              </th>
                              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Winning Bid
                              </th>
                              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Allocated Price
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {auction.lots
                              .filter(lot => lot.status === 'won')
                              .sort((a, b) => a.lot_number.localeCompare(b.lot_number, undefined, {numeric: true}))
                              .map(lot => (
                                <tr key={lot.id}>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                    {lot.lot_number}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {formatCurrency(
                                      parseFloat((lot.winning_bid || 0).toFixed(2)),
                                      auction.auction_house_currency
                                    )}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                                    {formatCurrency(allocatedPrices[lot.id], auction.user_currency)}
                                  </td>
                                </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50">
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                Total
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                {(() => {
                                  const total = auction.lots
                                    .filter(lot => lot.status === 'won')
                                    .sort((a, b) => a.lot_number.localeCompare(b.lot_number, undefined, {numeric: true}))
                                    .reduce((sum, lot) => {
                                      // Round each lot value to 2 decimal places before adding
                                      return sum + parseFloat((lot.winning_bid || 0).toFixed(2));
                                    }, 0);
                                  return formatCurrency(
                                    parseFloat(total.toFixed(2)),
                                    auction.auction_house_currency
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                {(() => {
                                  // Calculate sum of allocated prices
                                  const allocatedTotal = Object.values(allocatedPrices).reduce((sum, price) => sum + price, 0);
                                  return formatCurrency(
                                    parseFloat(allocatedTotal.toFixed(2)),
                                    auction.user_currency
                                  );
                                })()}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="mb-4 text-sm text-gray-500">No lots found</div>
                    )}
                    <div>
                      <label htmlFor="totalPrice" className="block text-sm font-medium text-gray-700">
                        Total Price Paid
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="totalPrice"
                          id="totalPrice"
                          required
                          value={totalPrice}
                          onChange={(e) => {
                            let value = e.target.value;
                            
                            // Keep only numbers and decimal points, ignore currency symbols
                            value = value.replace(/[^\d.]/g, '');
                            
                            // Ensure only one decimal point
                            const parts = value.split('.');
                            if (parts.length > 2) {
                              value = parts[0] + '.' + parts.slice(1).join('');
                            }
                            
                            setTotalPrice(value);
                          }}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Documents
                      </label>
                      <div className="space-y-2">
                        {uploadedDocuments.length > 0 ? (
                          <div className="space-y-2">
                            {uploadedDocuments.map(doc => (
                              <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                <span className="text-sm text-gray-900">{doc.title}</span>
                                <button
                                  type="button"
                                  onClick={() => setUploadedDocuments(prev => prev.filter(d => d.id !== doc.id))}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No documents attached</p>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowDocumentUpload(true)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Upload Document
                        </button>
                      </div>
                    </div>

                    {error && (
                      <p className="mt-2 text-sm text-red-600">{error}</p>
                    )}

                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        disabled={isImporting || !totalPrice}
                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-300 sm:col-start-2"
                      >
                        {isImporting ? 'Importing...' : 'Import Lots'}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Document Upload Form */}
      {showDocumentUpload && (
        <DocumentUploadForm
          open={showDocumentUpload}
          onClose={() => setShowDocumentUpload(false)}
          onSuccess={handleDocumentUploadSuccess}
          preSelectedCoins={new Set()}
        />
      )}
    </Transition.Root>
  );
}
